import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";

import { TRACKING_CARD_BLUE_SX } from "./trackingStyles";

const HorizontalStepNode = ({
  index,
  label,
  state,
  onClick,
  clickable,
  subtitle,
  hideNumber = false,
}) => {
  const colors = {
    completed: { bg: "success.main", text: "success.main", border: "success.light" },
    current: { bg: "primary.main", text: "primary.main", border: "primary.light" },
    future: { bg: "grey.300", text: "text.secondary", border: "divider" },
  };
  const c = colors[state] || colors.future;

  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        flex: "0 0 auto",
        width: { xs: 72, sm: 84 },
        textAlign: "center",
        cursor: clickable ? "pointer" : "default",
        px: 0.5,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: state === "future" ? "grey.100" : c.bg,
          color: state === "future" ? "text.secondary" : "common.white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 0.75,
          border: 2,
          borderColor: c.border,
          boxShadow: state === "current" ? "0 0 0 4px rgba(25,118,210,0.15)" : "none",
        }}
      >
        {state === "completed" ? (
          <CheckCircleIcon sx={{ fontSize: 20 }} />
        ) : hideNumber ? (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: state === "future" ? "grey.400" : "common.white",
            }}
          />
        ) : (
          <Typography variant="caption" fontWeight={700}>
            {(index + 1).toLocaleString("fa-IR")}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: state === "current" ? 700 : 500,
          color: c.text,
          lineHeight: 1.3,
          fontSize: "0.68rem",
        }}
      >
        {label}
      </Typography>
      {subtitle ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontSize: "0.62rem",
            mt: 0.25,
            lineHeight: 1.2,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
};

HorizontalStepNode.propTypes = {
  index: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  state: PropTypes.oneOf(["completed", "current", "future"]).isRequired,
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
  subtitle: PropTypes.string,
  hideNumber: PropTypes.bool,
};

const Connector = ({ active, flex = false, short = false }) => (
  <Box
    sx={{
      flex: flex ? 1 : "0 0 auto",
      width: flex ? "auto" : short ? 16 : 24,
      minWidth: flex ? 8 : short ? 16 : 24,
      height: 2,
      bgcolor: active ? "success.main" : "grey.300",
      mt: 2.25,
      mx: 0.25,
    }}
  />
);

const getParallelState = (step) => {
  if (step?.requestConfirm === "Completed") return "completed";
  if (step?.requestConfirm === "InProcess") return "current";
  return "future";
};

const ParallelBlock = ({ steps }) => {
  if (!steps?.length) return null;

  const blockState = steps.some((s) => getParallelState(s) === "current")
    ? "current"
    : steps.every((s) => getParallelState(s) === "completed")
      ? "completed"
      : "future";

  const borderColor =
    blockState === "completed"
      ? "success.light"
      : blockState === "current"
        ? "primary.light"
        : "grey.300";

  return (
    <Box
      sx={{
        flex: "0 0 auto",
        border: "2px dashed",
        borderColor,
        borderRadius: 2,
        px: { xs: 1, sm: 1.5 },
        py: 1,
        mx: 0.25,
        bgcolor: blockState === "current" ? "primary.50" : "background.paper",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        {steps.map((step, index) => (
          <Stack key={step.stepInfoId || step.stepTitle || index} direction="row" alignItems="flex-start">
            {index > 0 ? (
              <Connector active={getParallelState(step) !== "future"} short />
            ) : null}
            <HorizontalStepNode
              index={index}
              label={step.stepTitle}
              subtitle={step.statusTitle}
              state={getParallelState(step)}
              hideNumber
              clickable={false}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

ParallelBlock.propTypes = {
  steps: PropTypes.array,
};

const RequestWorkflowTracker = ({
  timelineSteps,
  parallelTimelineSteps = [],
  parallelInsertAfterMainIndex = 1,
  activeIndex,
  currentStageTitle,
  currentStageDescription,
  onStepSelect,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const parallelActive = parallelTimelineSteps.some(
    (s) => getParallelState(s) === "current"
  );

  const getMainNodeState = (index) => {
    if (index < activeIndex) return "completed";
    if (index === activeIndex) {
      if (index === parallelInsertAfterMainIndex && parallelActive) return "completed";
      return "current";
    }
    if (index === parallelInsertAfterMainIndex && parallelActive) return "completed";
    return "future";
  };

  const insertAt = Math.max(
    0,
    Math.min(parallelInsertAfterMainIndex, Math.max(timelineSteps.length - 1, 0))
  );
  const beforeParallel = timelineSteps.slice(0, insertAt + 1);
  const afterParallel = timelineSteps.slice(insertAt + 1);

  const renderSegment = (horizontalScroll = false) => (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{
        minWidth: horizontalScroll ? "min-content" : undefined,
        width: horizontalScroll ? undefined : "100%",
      }}
    >
      {beforeParallel.map((step, index) => (
        <Stack
          key={step.stepInfoId || step.stepTitle || `before-${index}`}
          direction="row"
          alignItems="flex-start"
          sx={{ flex: horizontalScroll ? "0 0 auto" : 1, minWidth: 0 }}
        >
          {index > 0 ? (
            <Connector
              active={getMainNodeState(index) !== "future" || index <= activeIndex}
              flex={!horizontalScroll}
            />
          ) : null}
          <HorizontalStepNode
            index={index}
            label={step.stepTitle}
            state={getMainNodeState(index)}
            clickable={index <= activeIndex && Boolean(onStepSelect)}
            onClick={() => onStepSelect?.(index)}
          />
        </Stack>
      ))}

      {parallelTimelineSteps.length > 0 ? (
        <>
          <Connector
            active={
              getMainNodeState(insertAt) === "completed" || parallelActive || activeIndex > insertAt
            }
            flex={!horizontalScroll}
            short={horizontalScroll}
          />
          <ParallelBlock steps={parallelTimelineSteps} />
        </>
      ) : null}

      {afterParallel.map((step, index) => {
        const mainIndex = insertAt + 1 + index;
        return (
          <Stack
            key={step.stepInfoId || step.stepTitle || `after-${index}`}
            direction="row"
            alignItems="flex-start"
            sx={{ flex: horizontalScroll ? "0 0 auto" : 1, minWidth: 0 }}
          >
            <Connector
              active={getMainNodeState(mainIndex) !== "future" || mainIndex <= activeIndex}
              flex={!horizontalScroll}
            />
            <HorizontalStepNode
              index={mainIndex}
              label={step.stepTitle}
              state={getMainNodeState(mainIndex)}
              clickable={mainIndex <= activeIndex && Boolean(onStepSelect)}
              onClick={() => onStepSelect?.(mainIndex)}
            />
          </Stack>
        );
      })}
    </Stack>
  );

  return (
    <Box sx={{ ...TRACKING_CARD_BLUE_SX, p: { xs: 2, md: 2.5 }, maxWidth: "100%" }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
        روند پیگیری درخواست
      </Typography>

      {isMobile ? (
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            maxWidth: "100%",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            mx: -0.5,
            px: 0.5,
          }}
        >
          {renderSegment(true)}
        </Box>
      ) : (
        renderSegment(false)
      )}

      {(currentStageTitle || currentStageDescription) && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: (t) => (t.palette.mode === "dark" ? "action.hover" : "primary.50"),
            border: "1px solid",
            borderColor: "primary.light",
          }}
        >
          {currentStageTitle ? (
            <Typography variant="subtitle2" fontWeight={800} color="primary.main" sx={{ mb: 0.5 }}>
              {currentStageTitle}
            </Typography>
          ) : null}
          {currentStageDescription ? (
            <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
              {currentStageDescription}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

RequestWorkflowTracker.propTypes = {
  timelineSteps: PropTypes.array.isRequired,
  parallelTimelineSteps: PropTypes.array,
  parallelInsertAfterMainIndex: PropTypes.number,
  activeIndex: PropTypes.number.isRequired,
  currentStageTitle: PropTypes.string,
  currentStageDescription: PropTypes.string,
  onStepSelect: PropTypes.func,
};

export default RequestWorkflowTracker;
